import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import CleanupLink from './CleanupLink';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faUser,
    faSignOutAlt,
    faTachometerAlt,
    faChevronDown,
} from "@fortawesome/free-solid-svg-icons";
import { getUser, logout } from '../services/authService';

const Header = () => {
  const [isScrolled,    setIsScrolled]    = useState(false);
  const [menuOpen,      setMenuOpen]      = useState(false);
  const [openDropdown,  setOpenDropdown]  = useState(null);
  const [user,          setUser]          = useState(getUser());

  const location = useLocation();
  const navigate = useNavigate();

  // Refresh user state on every route change (handles login/logout)
  useEffect(() => {
    setMenuOpen(false);
    setOpenDropdown(null);
    setUser(getUser());
  }, [location.pathname]);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleOutside = (e) => {
      if (!e.target.closest('.main-header') && !e.target.closest('.header-user-menu')) {
        setMenuOpen(false);
        setOpenDropdown(null);
      }
    };
    document.addEventListener('click', handleOutside);
    return () => document.removeEventListener('click', handleOutside);
  }, []);

  const toggleDropdown = (name) => {
    setOpenDropdown(prev => prev === name ? null : name);
  };

  const handleNavItemClick = () => {
    setMenuOpen(false);
    setOpenDropdown(null);
  };

  const handleDropdownMouseLeave = (name) => {
    setOpenDropdown(prev => (prev === name ? null : prev));
  };

  const handleLogout = (e) => {
    e.preventDefault();
    logout();
    setUser(null);
    setOpenDropdown(null);
    navigate('/login-signup');
  };

  // Initials for the avatar when logged in
  const initials = user
    ? (`${user.first_name?.[0] || ''}${user.last_name?.[0] || ''}`.toUpperCase() || user.name?.[0]?.toUpperCase() || '?')
    : null;

  return (
    <>
      <style>{`
        /* ── Logged-in user button ── */
        .header-user-btn {
          position: relative;
          display: inline-flex;
          align-items: center;
          gap: 7px;
          background: rgba(255,255,255,0.12);
          border: 1.5px solid rgba(255,255,255,0.25);
          border-radius: 50px;
          padding: 6px 14px 6px 6px;
          cursor: pointer;
          transition: background 0.2s, border-color 0.2s;
          color: #fff;
          font-family: inherit;
          font-size: 13.5px;
          font-weight: 700;
        }
        .main-header.scrolled .header-user-btn {
          border-color: rgba(0,37,54,0.18);
          color: var(--primary-color);
          background: rgba(0,37,54,0.05);
        }
        .header-user-btn:hover {
          background: rgba(255,255,255,0.22);
          border-color: rgba(255,255,255,0.4);
        }
        .main-header.scrolled .header-user-btn:hover {
          background: rgba(0,37,54,0.09);
        }
        .header-user-avatar {
          width: 28px;
          height: 28px;
          border-radius: 50%;
          background: linear-gradient(135deg, #00a8cc, #1d6a88);
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 11px;
          font-weight: 800;
          color: #fff;
          flex-shrink: 0;
          letter-spacing: 0;
        }
        .header-user-chevron {
          font-size: 10px;
          opacity: 0.7;
          transition: transform 0.2s;
        }
        .header-user-chevron.open { transform: rotate(180deg); }

        /* ── User dropdown ── */
        .header-user-dropdown {
          position: absolute;
          top: calc(100% + 10px);
          right: 0;
          min-width: 200px;
          background: #fff;
          border-radius: 14px;
          box-shadow: 0 8px 32px rgba(0,37,54,0.14), 0 2px 8px rgba(0,37,54,0.08);
          border: 1px solid rgba(0,37,54,0.07);
          overflow: hidden;
          z-index: 9999;
          animation: hud-pop 0.18s cubic-bezier(0.22,1,0.36,1) both;
        }
        @keyframes hud-pop {
          from { opacity: 0; transform: translateY(-6px) scale(0.97); }
          to   { opacity: 1; transform: translateY(0) scale(1); }
        }
        .header-user-dropdown-top {
          padding: 14px 16px 12px;
          border-bottom: 1px solid #eef1f5;
        }
        .header-user-dropdown-name {
          font-size: 13.5px;
          font-weight: 700;
          color: #002536;
          margin: 0 0 2px;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        .header-user-dropdown-email {
          font-size: 11.5px;
          color: #8a99ad;
          margin: 0;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        .header-user-dropdown-item {
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 11px 16px;
          font-size: 13.5px;
          font-weight: 600;
          color: #3a4a5c;
          text-decoration: none;
          transition: background 0.15s, color 0.15s;
          cursor: pointer;
          border: none;
          background: transparent;
          width: 100%;
          text-align: left;
          font-family: inherit;
        }
        .header-user-dropdown-item:hover {
          background: #f0f7fa;
          color: #1d6a88;
        }
        .header-user-dropdown-item.logout {
          color: #e53e3e;
          border-top: 1px solid #eef1f5;
        }
        .header-user-dropdown-item.logout:hover {
          background: #fff5f5;
          color: #c0392b;
        }
        .header-user-dropdown-item svg {
          width: 14px;
          flex-shrink: 0;
        }

        /* ── Wrapper needs position:relative ── */
        .header-user-menu {
          position: relative;
          display: inline-flex;
          align-items: center;
        }
      `}</style>

      <header className="header-wrapper">
        {/* Top Bar */}
        <div className="top-bar">
          <div className="sliding-text-container">
            <div className="sliding-text">
              <span>✨ Professional Embroidery Digitizing Services • Fast Turnaround</span>
              <span>🎯 High-Quality Vector Conversion • Precision & Detail Guaranteed</span>
              <span>🚀 24-Hour Rush Service Available • Same Day Delivery Options</span>
              <span>💼 Trusted by Businesses Worldwide • 100% Satisfaction Guarantee</span>
              <span>🎨 Custom Patch Design • Free Revisions on All Orders</span>
              <span>✨ Professional Embroidery Digitizing Services • Fast Turnaround</span>
              <span>🎯 High-Quality Vector Conversion • Precision & Detail Guaranteed</span>
            </div>
          </div>
        </div>

        {/* Main Header */}
        <nav className={`navbar navbar-expand-lg main-header ${isScrolled ? 'scrolled' : ''}`}>
          <div className="container">
            {/* Logo */}
            <CleanupLink className="navbar-brand" to="/" onClick={handleNavItemClick}>
              <div className="logo-container">
                <img src="/images/logo.png" alt="Grand Indigo Logo" />
              </div>
            </CleanupLink>

            {/* Mobile Toggle */}
            <button
              className="navbar-toggler"
              type="button"
              aria-label="Toggle navigation"
              onClick={() => setMenuOpen(prev => !prev)}
            >
              <span className="navbar-toggler-icon"></span>
            </button>

            {/* Navigation Menu */}
            <div className={`navbar-collapse ${menuOpen ? 'show' : 'collapse'}`}>
              <ul className="navbar-nav mx-auto">

                <li className="nav-item">
                  <CleanupLink className="nav-link" to="/" onClick={handleNavItemClick}>Home</CleanupLink>
                </li>

                {/* Services Mega Menu */}
                <li
                  className="nav-item dropdown mega-menu-item"
                  onMouseLeave={() => handleDropdownMouseLeave('services')}
                >
                  <a
                    className={`nav-link dropdown-toggle ${openDropdown === 'services' ? 'show' : ''}`}
                    href="#"
                    onClick={(e) => { e.preventDefault(); toggleDropdown('services'); }}
                  >
                    Services
                  </a>
                  <div className={`dropdown-menu mega-menu ${openDropdown === 'services' ? 'show' : ''}`}>
                    <div className="container">
                      <div className="row g-3">
                        {[
                          { to: '/services/embroidery-digitizing-services', img: '/images/emboiderydropdown.jpg', label: 'Embroidery Digitizing' },
                          { to: '/services/vectorization-services', img: '/images/vectorizationdropdown.jpg', label: 'Vectorization' },
                          { to: '/services/website-design-development-services', img: '/images/websitedropdown.jpg', label: 'Website Development' },
                          { to: '/services/logo-design-services', img: '/images/logodropdown.jpg', label: 'Logo Designing' },
                          { to: '/services/branding-design-services', img: '/images/brandingdropdown.jpg', label: 'Brandings' },
                        ].map(({ to, img, label }) => (
                          <div className="col-md-3 col-6" key={label}>
                            <CleanupLink to={to} className="mega-menu-item-link" onClick={handleNavItemClick}>
                              <img src={img} className="mega-menu-img d-none d-lg-block" alt={label} />
                              <span>{label}</span>
                            </CleanupLink>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </li>

                {/* Embroidery Digitizing Dropdown */}
                <li
                  className="nav-item dropdown"
                  onMouseLeave={() => handleDropdownMouseLeave('embroidery')}
                >
                  <a
                    className={`nav-link dropdown-toggle ${openDropdown === 'embroidery' ? 'show' : ''}`}
                    href="#"
                    onClick={(e) => { e.preventDefault(); toggleDropdown('embroidery'); }}
                  >
                    Embroidery Digitizing
                  </a>
                  <ul className={`dropdown-menu ${openDropdown === 'embroidery' ? 'show' : ''}`}>
                    {[
                      { to: '/services/custom-embroidery-digitizing-services',    label: 'Custom Embroidery Digitizing'    },
                      { to: '/services/logo-digitizing-for-embroidery-services',  label: 'Logo Digitizing for Embroidery'  },
                      { to: '/services/cap-hat-digitizing-services',              label: 'Cap / Hat Digitizing'            },
                      { to: '/services/3d-puff-embroidery-digitizing-services',   label: '3D Puff Embroidery Digitizing'   },
                      { to: '/services/flat-embroidery-digitizing-services',      label: 'Flat Embroidery Digitizing'      },
                      { to: '/services/left-chest-logo-digitizing-services',      label: 'Left Chest Logo Digitizing'      },
                      { to: '/services/jacket-back-digitizing-services',          label: 'Jacket Back Digitizing'          },
                      { to: '/services/sleeve-logo-digitizing-services',          label: 'Sleeve Logo Digitizing'          },
                      { to: '/services/applique-digitizing-services',             label: 'Applique Digitizing'             },
                      { to: '/services/chenille-embroidery-digitizing-services',  label: 'Chenille Embroidery Digitizing'  },
                      { to: '/services/patch-digitizing-services',                label: 'Patch Digitizing'                },
                      { to: '/services/badge-digitizing-services',                label: 'Badge Digitizing'                },
                      { to: '/services/monogram-digitizing-services',             label: 'Monogram Digitizing'             },
                      { to: '/services/towel-digitizing-services',                label: 'Towel Digitizing'                },
                    ].map(({ to, label }) => (
                      <li key={label}>
                        <CleanupLink className="dropdown-item" to={to} onClick={handleNavItemClick}>{label}</CleanupLink>
                      </li>
                    ))}
                  </ul>
                </li>

                {/* Pricing Dropdown */}
                <li
                  className="nav-item dropdown"
                  onMouseLeave={() => handleDropdownMouseLeave('pricing')}
                >
                  <a
                    className={`nav-link dropdown-toggle ${openDropdown === 'pricing' ? 'show' : ''}`}
                    href="#"
                    onClick={(e) => { e.preventDefault(); toggleDropdown('pricing'); }}
                  >
                    Pricing
                  </a>
                  <ul className={`dropdown-menu ${openDropdown === 'pricing' ? 'show' : ''}`}>
                    <li>
                      <CleanupLink className="dropdown-item" to="/pricing" onClick={handleNavItemClick}>Regular Pricing</CleanupLink>
                    </li>
                    <li>
                      <CleanupLink className="dropdown-item" to="/custom-patches-pricing" onClick={handleNavItemClick}>Custom Patches Pricing</CleanupLink>
                    </li>
                    <li>
                      <CleanupLink className="dropdown-item" to="/monthly-subscription" onClick={handleNavItemClick}>Monthly Subscription</CleanupLink>
                    </li>
                  </ul>
                </li>

                <li className="nav-item">
                  <CleanupLink className="nav-link" to="/portfolio" onClick={handleNavItemClick}>Portfolio</CleanupLink>
                </li>

                <li
                  className="nav-item dropdown"
                  onMouseLeave={() => handleDropdownMouseLeave('resources')}
                >
                  <a
                    className={`nav-link dropdown-toggle ${openDropdown === 'resources' ? 'show' : ''}`}
                    href="#"
                    onClick={(e) => { e.preventDefault(); toggleDropdown('resources'); }}
                  >
                    Resources
                  </a>
                  <ul className={`dropdown-menu ${openDropdown === 'resources' ? 'show' : ''}`}>
                    <li>
                      <CleanupLink className="dropdown-item" to="/blogs" onClick={handleNavItemClick}>Blogs</CleanupLink>
                    </li>
                    <li>
                      <CleanupLink className="dropdown-item" to="/print-on-demand" onClick={handleNavItemClick}>Print on Demand</CleanupLink>
                    </li>
                    <li>
                      <CleanupLink className="dropdown-item" to="/sewout" onClick={handleNavItemClick}>Sewout</CleanupLink>
                    </li>
                  </ul>
                </li>
              </ul>

              {/* ── CTA / User area ── */}
              <div className="header-cta">
                {user ? (
                  /* ── Logged in: avatar button with dropdown ── */
                  <div className="header-user-menu">
                    <button
                      className="header-user-btn"
                      onClick={(e) => { e.stopPropagation(); toggleDropdown('user'); }}
                    >
                      <div className="header-user-avatar">{initials}</div>
                      <span>{user.first_name || user.name?.split(' ')[0] || 'Account'}</span>
                      <FontAwesomeIcon
                        icon={faChevronDown}
                        className={`header-user-chevron${openDropdown === 'user' ? ' open' : ''}`}
                      />
                    </button>

                    {openDropdown === 'user' && (
                      <div className="header-user-dropdown">
                        <div className="header-user-dropdown-top">
                          <p className="header-user-dropdown-name">
                            {user.name || `${user.first_name} ${user.last_name}`}
                          </p>
                          <p className="header-user-dropdown-email">{user.email}</p>
                        </div>
                        <CleanupLink
                          to="/user-dashboard"
                          className="header-user-dropdown-item"
                          onClick={() => setOpenDropdown(null)}
                        >
                          <FontAwesomeIcon icon={faTachometerAlt} />
                          My Dashboard
                        </CleanupLink>
                        <button
                          className="header-user-dropdown-item logout"
                          onClick={handleLogout}
                        >
                          <FontAwesomeIcon icon={faSignOutAlt} />
                          Sign Out
                        </button>
                      </div>
                    )}
                  </div>
                ) : (
                  /* ── Logged out: plain icon button ── */
                  <CleanupLink to="/login-signup" className="btn btn-primary btn-dashboard">
                    <FontAwesomeIcon icon={faUser} />
                  </CleanupLink>
                )}

                <CleanupLink to="/contact" className="btn btn-primary">Contact Us</CleanupLink>
              </div>
            </div>
          </div>
        </nav>
      </header>
    </>
  );
};

export default Header;